import { css } from '@emotion/react';
import { formatDate } from '@frontend/date';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Text, CopyToClipboardButton } from '@frontend/design-system';
import { DATETIME_FORMATS, PRIMARY_EVENT_COLOR } from '../../../constants';
import { stripDateOrdinals } from '../../../utils';
import { OverlappingEventIndicator } from '../../calendar-view/calendar-view-v3/overlapping-event-indicator';
import { AppointmentStatusV2 } from '../../schedule-calendar-components/appointment-event-card/appointment-status-v2';
import { EventCardAppointmentStatusChipV3 } from '../../v3/EventCardAppointmentStatusChip';
import { ColorDot } from '../color-dot';
import { useApptPanelContext } from './appt-panel-context';
import { DetailItem } from './item-detail';
import { PanelTabs } from './panel-tabs';

export const PanelDetails = () => {
  const { apptDetails, isV3Enabled, locationId, shouldShowOperatory } = useApptPanelContext();
  const { getScopeName } = useAppScopeStore();

  const details = apptDetails?.appointmentDetails;
  const apptId = apptDetails?.id ?? '';
  const apptTypeName = details?.type || '';
  const apptTypeColor = details?.displayOptions?.primaryColor || PRIMARY_EVENT_COLOR;
  const providerName = details?.practitioner ?? '';
  const operatoryName = details?.operatory || '';
  const status = details?.status;
  const startDate = details?.date ? formatDate(stripDateOrdinals(details?.date), DATETIME_FORMATS.ISO_DATE) : '';
  const referenceId = details?.referenceId || '';

  // TODO: find proper way to determine archive status
  // and remove useGetEntityArchiveStatus usage from everywhere
  const isApptTypeArchived = true;
  const isProviderArchived = true;
  return (
    <div css={contentContainer}>
      {status &&
        (isV3Enabled ? (
          <EventCardAppointmentStatusChipV3
            locationId={locationId}
            appointmentId={apptId}
            appointmentStatus={status}
            appointmentStartDate={startDate}
            appointmentReferenceId={referenceId}
          />
        ) : (
          <AppointmentStatusV2 locationId={locationId} appointmentId={apptId} status={status} />
        ))}
      <DetailItem
        title={getScopeName(locationId)}
        leftElement={<Icon name='location-small' color='light' />}
        rightElement={<CopyToClipboardButton css={{ height: 24, width: 24 }} variant='tertiary' textToCopy={'test'} />}
      />
      <DetailItem
        title={formatDate(startDate, 'ddd, MMMM DD, YYYY')}
        leftElement={<Icon name='calendar-small' color='light' />}
        rightElement={
          isV3Enabled && <OverlappingEventIndicator variant='full' visible={details?.isOverlapping} borderless />
        }
      >
        <Text size='small' color='light'>
          {details?.duration}
        </Text>
      </DetailItem>
      {!!apptTypeName && (
        <DetailItem
          isArchived={isApptTypeArchived}
          title={apptTypeName}
          leftElement={<ColorDot color={apptTypeColor} />}
        />
      )}
      {shouldShowOperatory && !!operatoryName && (
        <DetailItem title={operatoryName} leftElement={<Icon name='department-small' color='light' />} />
      )}
      {!!providerName && (
        <DetailItem
          title={providerName}
          isArchived={isProviderArchived}
          leftElement={<Icon name='user-small' color='light' />}
        />
      )}
      <PanelTabs />
    </div>
  );
};

const contentContainer = css({
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
  gap: theme.spacing(1),
});
